import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useProjectUpdates } from "@/hooks/use-project-updates";
import { apiClient } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Clock, FileText, Globe, Layers, Target, DollarSign, Users, CheckCircle2, Download, File, Eye, Lightbulb, ScrollText, Pencil, Save, X, Sparkles, Zap, TrendingUp, Pin, GripVertical, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { renderAsync } from "docx-preview";
import { RecommendationCard } from "@/components/score/RecommendationCard";
import { ProjectAssetsSection } from "@/components/ProjectAssetsSection";
import { AdminProjectAssetsSection } from "@/components/AdminProjectAssetsSection";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

export default function AdminProjectDetailsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditScoreOpen, setIsEditScoreOpen] = useState(false);
  const [isEditRecOpen, setIsEditRecOpen] = useState(false);
  const [editingRec, setEditingRec] = useState<any>(null);
  const [tacticsV2Data, setTacticsV2Data] = useState<any>(null);
  const [tacticsV2Loading, setTacticsV2Loading] = useState(false);
  const [isPinDialogOpen, setIsPinDialogOpen] = useState(false);
  const [pinningTactic, setPinningTactic] = useState<any>(null);
  const [isEditWordingDialogOpen, setIsEditWordingDialogOpen] = useState(false);
  const [editingTactic, setEditingTactic] = useState<any>(null);
  const [isHideConfirmOpen, setIsHideConfirmOpen] = useState(false);
  const [hidingTactic, setHidingTactic] = useState<any>(null);
  const [isUploadArtifactOpen, setIsUploadArtifactOpen] = useState(false);
  const [uploadingTactic, setUploadingTactic] = useState<any>(null);
  const [isProcessingArtifact, setIsProcessingArtifact] = useState(false);
  const [isEditDetailsOpen, setIsEditDetailsOpen] = useState(false);
  const [allTactics, setAllTactics] = useState<any[]>([]);
  const [isAddTacticDialogOpen, setIsAddTacticDialogOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<{ blob: Blob, name: string } | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const queryClient = useQueryClient();

  const scriptInputRef = useRef<HTMLInputElement>(null);
  const [showScriptUploadDialog, setShowScriptUploadDialog] = useState(false);
  const [scriptToUpload, setScriptToUpload] = useState<File | null>(null);
  const [scriptUploadReason, setScriptUploadReason] = useState("");
  const [isUploadingScript, setIsUploadingScript] = useState(false);
  const [isProcessingScript, setIsProcessingScript] = useState(false);
  const { lastUpdate: projectUpdates, isConnected } = useProjectUpdates(projectId || "");

  useEffect(() => {
    // Check if projectUpdates has the structure { messages: ... } or is an array
    const updatesArray = Array.isArray(projectUpdates)
      ? projectUpdates
      : (projectUpdates as any)?.messages || [];

    if (updatesArray && Array.isArray(updatesArray) && updatesArray.length > 0) {
      const latestUpdate = updatesArray[updatesArray.length - 1];

      // Look for completion indicators: 100% progress, specific statuses, or any score_data in the event stream
      if (latestUpdate && (
        latestUpdate.status === "analysis_complete" ||
        latestUpdate.progress === 100 ||
        latestUpdate.percent === 100 ||
        latestUpdate.status === "Complete!" ||
        latestUpdate.status === "Existing score found" ||
        latestUpdate.score_data ||
        latestUpdate.status === "Regeneration Complete!" ||
        latestUpdate.event === "complete"
      )) {
        if (isProcessingScript || project?.status === 'script_uploaded' || project?.status === 'processing' || project?.status === 'analysis_in_progress') {
          setIsProcessingScript(false);
          toast.success("Score calculation complete!");
          fetchData();
        }
        if (isProcessingArtifact) {
          setIsProcessingArtifact(false);
          toast.success("Score recalculation complete!");
          fetchData();
        }
      } else if (latestUpdate && (
        latestUpdate.status === "script_uploaded" ||
        latestUpdate.status === "processing" ||
        latestUpdate.status === "analysis_in_progress" ||
        latestUpdate.percent > 0 ||
        latestUpdate.status?.includes("Script:") ||
        latestUpdate.status?.includes("Retrieving") ||
        latestUpdate.status?.includes("Generating") ||
        latestUpdate.status?.includes("Recalculating") ||
        latestUpdate.status?.includes("Regenerating")
      )) {
        if (!isProcessingScript && !isProcessingArtifact) {
          // If we just started a manual script upload, it's fine.
          // But if we just loaded the page and there's a lingering SSE, we should check actual project status.
          if (project && project.status !== 'analysis_complete') {
            setIsProcessingScript(true);
          }
        }
      }
    }
  }, [projectUpdates, isProcessingScript, isProcessingArtifact, project]);

  const handleScriptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 50 * 1024 * 1024) {
      toast.error("Script size must be less than 50MB.");
      return;
    }

    setScriptToUpload(file);
    setShowScriptUploadDialog(true);
  };

  const handleConfirmScriptUpload = async () => {
    if (!scriptToUpload || !project?._id) return;
    if (!scriptUploadReason.trim()) {
      toast.error("Please provide a reason for this override.");
      return;
    }

    setIsUploadingScript(true);
    try {
      const response = await apiClient.uploadProjectScriptOverride(project._id, scriptToUpload, scriptUploadReason);
      toast.success("Script uploaded successfully. Score calculation in progress.");
      setShowScriptUploadDialog(false);
      setScriptToUpload(null);
      setScriptUploadReason("");

      // Invalidate the projects query to force a refetch on the projects page
      queryClient.invalidateQueries({ queryKey: ["adminProjects"] });

      // Return to project listing instead of waiting
      navigate("/admin/projects");
    } catch (error: any) {
      console.error("Script upload failed:", error);
      toast.error(error.message || "An error occurred during upload.");
    } finally {
      setIsUploadingScript(false);
      if (scriptInputRef.current) {
        scriptInputRef.current.value = "";
      }
    }
  };

  // Helper to refresh tactics data
  const refreshTacticsData = async (data: any, tacticsDefs: any[]) => {
    if (data.artie_score && data.artie_score.current_recommendations) {
      setTacticsV2Loading(true);
      try {
        // For admin view, show ALL recommendations regardless of tactic definition active status
        const currentRecommendations = data.artie_score.current_recommendations || [];

        // Fetch project overrides to include hidden status
        let overrides: Record<string, any> = {};
        try {
          // We need to know which tactics are hidden, but that data isn't in current_recommendations
          // This logic was relying on select_top_recommendations from backend which filters out hidden ones
          // But now we want to SHOW hidden ones in admin.
          // Since we can't easily change the backend response structure for just admin without affecting user view,
          // we need to fetch overrides separately or rely on backend to pass 'is_hidden' flag if requested.
          // For now, let's assume the backend 'select_top_recommendations' modification was done correctly
          // to INCLUDE hidden tactics if they are requested by admin?
          // Wait, the previous backend change was:

          /*
           if scored_tactic:
               tactic = scored_tactic["tactic"]
               
               # Check for overrides
               override = overrides.get(tactic["_id"])
               if override and override.get("is_hidden"):
                   continue  <-- This SKIPS hidden tactics!
          */

          // Correct. The backend logic I modified previously SKIPS hidden tactics entirely from the recommendation list.
          // This means they won't appear in 'current_recommendations' array at all.
          // So the frontend never sees them to display a "Hidden" badge.

          // To fix this for the ADMIN view, we need to:
          // 1. Fetch the project overrides on the frontend.
          // 2. Or modify backend to include hidden tactics with a flag.

          // Since we don't have a direct endpoint to fetch just overrides, let's use the second approach:
          // But modifying backend affects the user view too (user shouldn't see hidden tactics).

          // Better approach for Admin UI:
          // Use the getAllTactics list (allTactics) to cross-reference.
          // But 'recommendations' are specific to the project context (scores, reasoning).
          // We can't just display raw tactics from the bank.

          // Let's modify the backend service to return hidden tactics but mark them as 'is_hidden' in the response.
          // Then we filter them out in the User UI, but keep them in Admin UI.
          // Wait, the User UI uses the same 'current_recommendations' array from Artie Score object.

          // So if we include them in DB, the User will see them unless we filter in frontend.
          // Let's check where the user sees recommendations.
          // It's likely in 'frontend/src/pages/ProjectDetailsPage.tsx' or 'ScoreV2Page.tsx'.

          // To avoid touching User UI code and risking regressions there,
          // we should keep the backend filtering hidden tactics for the persisted list.

          // However, the Admin needs to see them to "Unhide" them.
          // This implies we need a way to fetch "potential but hidden" recommendations.

          // Actually, the simplest fix is to NOT filter them in backend persistence,
          // but mark them `is_hidden: true`.
          // Then update the User-facing components to filter out `is_hidden: true`.

          // Let's check 'backend/app/services/tactics_engine_v2.py' again.

        } catch (e) { }

        const allRecommendations = currentRecommendations
          .sort((a: any, b: any) => (a.priority_rank || 0) - (b.priority_rank || 0));

        // Transform the data to match the expected structure
        const tacticsData = {
          project_id: projectId,
          artie_score: data.artie_score.artie_score || 75,
          creator_phase: data.artie_score.creator_phase || "growth",
          recommendations: allRecommendations.map((rec: any) => ({
            ...rec,
            // Ensure required fields are present with fallbacks
            priority_score: rec.impact_score || 0,
            impact_tier: rec.impact_score >= 15 ? "HIGH" : rec.impact_score >= 8 ? "MEDIUM" : "LOW",
            actionability_tier: rec.actionability_score >= 4 ? "HIGH" : rec.actionability_score >= 2 ? "MEDIUM" : "LOW",
            reason: rec.tactic_description || rec.gap_description || "",
            templates: {
              what_to_improve: rec.tactic_name || "",
              why_it_matters: rec.grounding || rec.gap_description || "",
              how_to_improve: rec.tactic_description || ""
            },
            is_pinned: rec.is_pinned || false, // Add is_pinned support
            pinned_reason: rec.pinned_reason || "" // Add pinned_reason support
          })),
          total_eligible_tactics: currentRecommendations.length,
          generated_at: data.artie_score.created_at || new Date().toISOString()
        };

        setTacticsV2Data(tacticsData);
      } catch (tacticsError) {
        console.error("Failed to process tactics v2 recommendations:", tacticsError);
      } finally {
        setTacticsV2Loading(false);
      }
    }
  };

  const fetchData = async () => {
    if (!projectId) return;
    try {
      const [projectData, tacticsData, overridesData] = await Promise.all([
        apiClient.getAdminProject(projectId),
        apiClient.getAllTactics(1, 100), // Fetch all tactics
        apiClient.getProjectTacticOverrides(projectId)
      ]);

      if (projectData.artie_score && projectData.artie_score.current_recommendations) {
        projectData.artie_score.current_recommendations.forEach((rec: any) => {
          const override = overridesData.find((o: any) => o.tactic_id === rec.tactic_id);
          if (override) {
            rec.is_hidden = override.is_hidden;
          }
        });
      }

      setProject(projectData);
      setAllTactics(tacticsData.items);

      // Extract tactics v2 recommendations from project's artie_score.current_recommendations
      refreshTacticsData(projectData, tacticsData.items);
    } catch (error: any) {
      console.error("Failed to fetch project:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Failed to load project details";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [projectId]);

  // Handle case where project status resolves independently of SSE (e.g. initial load)
  useEffect(() => {
    if (project && project.status === 'analysis_complete') {
      if (isProcessingScript) setIsProcessingScript(false);
      if (isProcessingArtifact) setIsProcessingArtifact(false);
    }

    // In case there are no projectUpdates or the SSE disconnects, let's poll project status slightly differently or fallback
    const updatesArray = Array.isArray(projectUpdates)
      ? projectUpdates
      : (projectUpdates as any)?.messages || [];

    if (updatesArray && Array.isArray(updatesArray) && updatesArray.length > 0) {
      const latestMessage = updatesArray[updatesArray.length - 1];

      // Sometimes it sends final status (early_stage, growth, in_progress, analysis_complete) when it's genuinely done.
      // Other times we get 'Regeneration Complete!' or event='complete'.
      const isCompleteState =
        latestMessage?.status === "early_stage" ||
        latestMessage?.status === "in_progress" ||
        latestMessage?.status === "analysis_complete" ||
        latestMessage?.status === "growth" ||
        latestMessage?.status === "Complete!" ||
        latestMessage?.status === "Existing score found" ||
        latestMessage?.status === "Regeneration Complete!" ||
        latestMessage?.event === "complete" ||
        latestMessage?.percent === 100 ||
        latestMessage?.progress === 100;

      // Check if we actually have processing status from API explicitly too (when SSE sends a random intermediate message but project is already done in DB)
      const isProjectActuallyComplete = project?.status === "analysis_complete";

      if (isCompleteState || isProjectActuallyComplete) {
        if (isProcessingScript || isProcessingArtifact || project?.status === 'processing' || project?.status === 'analysis_in_progress') {

          // Only show toast if we were actively waiting on this page, and avoid firing constantly
          if (isProcessingScript || isProcessingArtifact) {
            toast.success("Score recalculation complete!");
          }

          setIsProcessingScript(false);
          setIsProcessingArtifact(false);

          // Make sure our local state matches the DB by forcing a refetch
          if (!isProjectActuallyComplete) {
            fetchData();
          }
        }
      }
    }
  }, [project?.status, isProcessingScript, isProcessingArtifact, projectUpdates]);

  const handleScoreUpdate = async (updatedScore: any) => {
    setProject((prev: any) => ({
      ...prev,
      artie_score: updatedScore
    }));

    // Fetch fresh project data to get the reprioritized recommendations
    try {
      const freshProjectData = await apiClient.getAdminProject(projectId!);
      setProject(freshProjectData);
      refreshTacticsData(freshProjectData, allTactics);
    } catch (error) {
      console.error("Failed to refresh project data after score update:", error);
      // Fallback to just updating the score if fetch fails
    }
  };

  const handleRecommendationUpdate = (updatedScore: any) => {
    setProject((prev: any) => ({
      ...prev,
      artie_score: updatedScore
    }));
    setIsEditRecOpen(false);
    setEditingRec(null);
  };

  const openRecEdit = (recId: string) => {
    if (!project?.artie_score?.recommendations) return;
    const rec = project.artie_score.recommendations.find((r: any) => r.id === recId || r.what_to_improve === recId);
    if (rec) {
      setEditingRec(rec);
      setIsEditRecOpen(true);
    }
  };

  const openPinDialog = (tactic: any) => {
    setPinningTactic(tactic);
    setIsPinDialogOpen(true);
  };

  const handleReviewToggle = async (currentStatus: boolean) => {
    if (!project) return;
    try {
      // Optimistic update
      setProject((prev: any) => ({ ...prev, is_reviewed: !currentStatus }));

      const response = await apiClient.toggleProjectReviewStatus(project._id, !currentStatus) as any;

      // Show success message
      toast.success(!currentStatus ? "Project marked as reviewed" : "Project marked as pending");

      // Show email notification result if project was approved
      if (!currentStatus && response?.email_notification) {
        if (response.email_notification.success) {
          toast.success(`Email sent to ${response.email_notification.email}`, {
            duration: 4000,
          });
        } else {
          toast.warning(`Failed to send email: ${response.email_notification.message}`, {
            duration: 5000,
          });
        }
      }

      // Invalidate the query for the single project to refetch the data
      // Optimistically update the project list cache
      queryClient.setQueriesData({ queryKey: ['adminProjects'] }, (oldData: any) => {
        if (!oldData) return oldData;

        const isPaginated = oldData.hasOwnProperty('items');
        const list = isPaginated ? oldData.items : oldData;

        const updatedList = list.map((p: any) =>
          p._id === project._id ? { ...p, is_reviewed: !currentStatus } : p
        );

        return isPaginated ? { ...oldData, items: updatedList } : updatedList;
      });

      // Invalidate queries to ensure eventual consistency
      queryClient.invalidateQueries({ queryKey: ["adminProject", project._id] });
      queryClient.invalidateQueries({ queryKey: ["adminProjects"], refetchType: 'none' }); // Invalidate but don't refetch immediately

    } catch (error: any) {
      console.error("Failed to update status:", error);
      toast.error(error.message || "Failed to update status");
      // Revert
      setProject((prev: any) => ({ ...prev, is_reviewed: currentStatus }));
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="flex justify-between">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-3 gap-6">
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] space-y-4">
        <div className="text-xl font-semibold text-foreground">Unable to View Project</div>
        <p className="text-muted-foreground text-center max-w-md">{error}</p>
        <Button onClick={() => navigate("/admin/projects")}>Back to Projects</Button>
      </div>
    );
  }

  function EditScoreDialog({ project, isOpen, setIsOpen, onScoreUpdate }: { project: any, isOpen: boolean, setIsOpen: (open: boolean) => void, onScoreUpdate: (score: any) => void }) {
    const [score, setScore] = useState<number>(project.artie_score?.artie_score || 0);
    const [reachStatus, setReachStatus] = useState<string>(project.artie_score?.reach_status || 'yellow');
    const [brandFitStatus, setBrandFitStatus] = useState<string>(project.artie_score?.brand_fit_status || 'yellow');
    const [prodStatus, setProdStatus] = useState<string>(project.artie_score?.production_readiness_status || 'yellow');
    const [reason, setReason] = useState<string>(project.artie_score?.override_reason || '');
    const [isSaving, setIsSaving] = useState(false);

    // Reset form when dialog opens
    useEffect(() => {
      if (isOpen && project.artie_score) {
        setScore(project.artie_score.artie_score);
        setReachStatus(project.artie_score.reach_status);
        setBrandFitStatus(project.artie_score.brand_fit_status);
        setProdStatus(project.artie_score.production_readiness_status);
        setReason(project.artie_score.override_reason || '');
      }
    }, [isOpen, project.artie_score]);

    const handleSave = async () => {
      if (!reason.trim()) {
        toast.error("Please provide a reason for the override");
        return;
      }

      setIsSaving(true);
      try {
        const updatedScore = await apiClient.overrideArtieScore(project.artie_score._id, {
          artie_score: score,
          reach_status: reachStatus,
          brand_fit_status: brandFitStatus,
          production_readiness_status: prodStatus,
          override_reason: reason
        });

        onScoreUpdate(updatedScore);
        toast.success("Artie Score updated successfully");
        setIsOpen(false);
      } catch (error: any) {
        console.error("Failed to update score:", error);
        toast.error(error.message || "Failed to update score");
      } finally {
        setIsSaving(false);
      }
    };

    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="font-cabinet text-xl">Edit Artie Score</DialogTitle>
            <DialogDescription className="font-ibm">
              Manually override the AI-generated score and metrics. This action will be logged.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="score" className="text-right font-ibm">
                Total Score
              </Label>
              <Input
                id="score"
                type="number"
                min="0"
                max="100"
                value={score}
                onChange={(e) => setScore(Number(e.target.value))}
                className="col-span-3 font-ibm"
              />
            </div>
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-foreground font-ibm">Sub-Score Overrides</h4>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="reach" className="text-right font-ibm">
                  Reach Potential
                </Label>
                <Select value={reachStatus} onValueChange={setReachStatus}>
                  <SelectTrigger className="col-span-3 font-ibm">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="green">🟢 Green (Strong)</SelectItem>
                    <SelectItem value="yellow">🟡 Yellow (Fair)</SelectItem>
                    <SelectItem value="red">🔴 Red (Weak)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="brand-fit" className="text-right font-ibm">
                  Brand Fit
                </Label>
                <Select value={brandFitStatus} onValueChange={setBrandFitStatus}>
                  <SelectTrigger className="col-span-3 font-ibm">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="green">🟢 Green (Strong)</SelectItem>
                    <SelectItem value="yellow">🟡 Yellow (Fair)</SelectItem>
                    <SelectItem value="red">🔴 Red (Weak)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="prod-readiness" className="text-right font-ibm">
                  Production Readiness
                </Label>
                <Select value={prodStatus} onValueChange={setProdStatus}>
                  <SelectTrigger className="col-span-3 font-ibm">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="green">🟢 Green (Strong)</SelectItem>
                    <SelectItem value="yellow">🟡 Yellow (Fair)</SelectItem>
                    <SelectItem value="red">🔴 Red (Weak)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="reason" className="font-ibm">
                Reason for Override <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="reason"
                placeholder="Explain why you are changing the score..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="font-ibm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)} disabled={isSaving} className="font-ibm">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving} className="font-ibm">
              {isSaving ? (
                <>
                  <span className="animate-spin mr-2">⏳</span> Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" /> Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  function PinTacticDialog({ project, tactic, isOpen, setIsOpen, onUpdate }: { project: any, tactic: any, isOpen: boolean, setIsOpen: (open: boolean) => void, onUpdate: (data: any) => void }) {
    const [selectedRank, setSelectedRank] = useState<string>(tactic?.priority_rank?.toString() || "1");
    const [reason, setReason] = useState<string>(tactic?.pinned_reason || "");
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
      if (isOpen && tactic) {
        setSelectedRank(tactic.priority_rank?.toString() || "1");
        setReason(tactic.pinned_reason || "");
      }
    }, [isOpen, tactic]);

    const handlePin = async () => {
      if (!project?._id || !tactic?.tactic_id) return;

      if (!reason.trim()) {
        toast.error("Please provide a reason for pinning this tactic");
        return;
      }

      setIsSaving(true);
      try {
        await apiClient.pinTactic(project._id, tactic.tactic_id, parseInt(selectedRank), reason);

        // Refresh project data to get updated score with new order
        const updatedProject = await apiClient.getAdminProject(project._id);

        // Update local state via parent callback
        if (updatedProject.artie_score) {
          // Update the full project state with new score data
          setProject((prev: any) => ({
            ...prev,
            artie_score: updatedProject.artie_score
          }));

          // Also refresh the specific tactics data structure
          refreshTacticsData(updatedProject, allTactics);
        }

        toast.success(`Tactic pinned to rank ${selectedRank}`);
        setIsOpen(false);
      } catch (error: any) {
        console.error("Failed to pin tactic:", error);
        toast.error(error.message || "Failed to pin tactic");
      } finally {
        setIsSaving(false);
      }
    };

    const handleUnpin = async () => {
      if (!project?._id || !tactic?.tactic_id) return;
      setIsSaving(true);
      try {
        await apiClient.unpinTactic(project._id, tactic.tactic_id);

        // Refresh project data
        const updatedProject = await apiClient.getAdminProject(project._id);

        // Update local state
        if (updatedProject.artie_score) {
          setProject((prev: any) => ({
            ...prev,
            artie_score: updatedProject.artie_score
          }));
          refreshTacticsData(updatedProject, allTactics);
        }

        toast.success("Tactic unpinned");
        setIsOpen(false);
      } catch (error: any) {
        console.error("Failed to unpin tactic:", error);
        toast.error(error.message || "Failed to unpin tactic");
      } finally {
        setIsSaving(false);
      }
    };

    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="font-cabinet text-xl flex items-center gap-2">
              <Pin className="h-5 w-5" />
              Manage Priority Rank
            </DialogTitle>
            <DialogDescription className="font-ibm">
              Pinning this tactic will force it to the selected rank, overriding the automated score-based ordering.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="bg-secondary/30 p-3 rounded-lg border border-border">
              <span className="text-xs font-semibold text-muted-foreground uppercase block mb-1">Tactic</span>
              <p className="font-medium text-sm">{tactic?.tactic_name}</p>
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-medium">Select Priority Rank</Label>
              <div className="grid grid-cols-5 gap-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rank) => (
                  <div
                    key={rank}
                    onClick={() => setSelectedRank(rank.toString())}
                    className={`
                      cursor-pointer rounded-lg border p-2 text-center transition-all
                      ${selectedRank === rank.toString()
                        ? 'bg-primary/10 border-primary text-primary ring-1 ring-primary'
                        : 'bg-background border-input hover:bg-accent hover:text-accent-foreground'}
                    `}
                  >
                    <div className="text-sm font-bold">{rank}</div>
                    <div className="text-[8px] uppercase font-medium text-muted-foreground">Rank</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Reason for Override <span className="text-red-500">*</span></Label>
              <Textarea
                placeholder="Why are you pinning this tactic? e.g., 'Critical brand safety issue' or 'High impact for this specific niche'"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="min-h-[80px]"
              />
            </div>

            {tactic?.is_pinned && (
              <div className="space-y-2 bg-amber-50 p-3 rounded border border-amber-200">
                <div className="flex items-center gap-2 text-xs text-amber-700 font-semibold">
                  <Pin className="h-3 w-3" />
                  Currently pinned to Rank {tactic.priority_rank}
                </div>
                {tactic.pinned_reason && (
                  <p className="text-xs text-amber-800 italic">
                    "{tactic.pinned_reason}"
                  </p>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="flex gap-2 sm:justify-between">
            {tactic?.is_pinned ? (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleUnpin}
                disabled={isSaving}
                className="w-full sm:w-auto"
              >
                Unpin Tactic
              </Button>
            ) : (
              <div /> // Spacer
            )}
            <div className="flex gap-2 w-full sm:w-auto justify-end">
              <Button variant="outline" size="sm" onClick={() => setIsOpen(false)} disabled={isSaving}>
                Cancel
              </Button>
              <Button size="sm" onClick={handlePin} disabled={isSaving}>
                {isSaving ? "Saving..." : "Save Rank"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  function UploadArtifactDialog({ project, tactic, isOpen, setIsOpen, queryClient, navigate, onUpdate }: { project: any, tactic: any, isOpen: boolean, setIsOpen: (open: boolean) => void, queryClient: any, navigate: any, onUpdate: () => void }) {
    const [fileToUpload, setFileToUpload] = useState<File | null>(null);
    const [reason, setReason] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const delay = (ms: number) => new Promise(res => setTimeout(res, ms));


    const handleSave = async () => {
      if (!project?._id || !tactic) return;
      if (!fileToUpload) {
        toast.error("Please select a file to upload");
        return;
      }
      if (!reason.trim()) {
        toast.error("Please provide a reason for uploading this artifact");
        return;
      }

      setIsSaving(true);
      try {
        const recommendationId = tactic.tactic_id || tactic.id || tactic.what_to_improve;
        await apiClient.uploadRecommendationArtifactOverride(project._id, recommendationId, fileToUpload, reason);
        toast.success("Artifact uploaded, refreshing details...");

        setFileToUpload(null);
        setReason("");

        await onUpdate();
        setIsOpen(false);

        toast.info("Score recalculation triggered. You will be redirected shortly.");

        await delay(3000); // Wait for user to see the change

        queryClient.invalidateQueries({ queryKey: ["adminProjects"] });
        navigate("/admin/projects");

      } catch (error: any) {
        console.error("Failed to upload artifact:", error);
        toast.error(error.message || "Failed to upload artifact");
      } finally {
        setIsSaving(false);
      }
    };

    return (
      <Dialog open={isOpen} onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) {
          setFileToUpload(null);
          setReason("");
        }
      }}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="font-cabinet text-xl flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upload Artifact Override
            </DialogTitle>
            <DialogDescription className="font-ibm">
              Upload an artifact to fulfill this recommendation. This action will be logged and trigger an automatic score recalculation.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="bg-secondary/30 p-3 rounded-lg border border-border">
              <span className="text-xs font-semibold text-muted-foreground uppercase block mb-1">Tactic</span>
              <p className="font-medium text-sm">{tactic?.tactic_name || tactic?.what_to_improve}</p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Select File</Label>
              <Input
                type="file"
                ref={fileInputRef}
                onChange={(e) => setFileToUpload(e.target.files?.[0] || null)}
                disabled={isSaving}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Reason for Override <span className="text-red-500">*</span></Label>
              <Textarea
                placeholder="Why are you uploading this artifact? e.g., 'Creator provided proof via email'"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="min-h-[80px]"
                disabled={isSaving}
              />
            </div>
          </div>

          <DialogFooter className="flex gap-2 sm:justify-end">
            <Button variant="outline" size="sm" onClick={() => setIsOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={isSaving || !fileToUpload || !reason.trim()}>
              {isSaving ? "Uploading..." : "Upload & Recalculate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  function EditRecommendationDialog({ project, recommendation, isOpen, setIsOpen, onUpdate }: { project: any, recommendation: any, isOpen: boolean, setIsOpen: (open: boolean) => void, onUpdate: (score: any) => void }) {
    const [title, setTitle] = useState(recommendation.what_to_improve || "");
    const [why, setWhy] = useState(recommendation.why_it_matters || "");
    const [how, setHow] = useState(recommendation.how_to_improve || "");
    const [priority, setPriority] = useState<string>(String(recommendation.priority || "1"));
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
      if (isOpen && recommendation) {
        setTitle(recommendation.what_to_improve || "");
        setWhy(recommendation.why_it_matters || "");
        setHow(recommendation.how_to_improve || "");
        setPriority(String(recommendation.priority || "1"));
      }
    }, [isOpen, recommendation]);

    const handleSave = async () => {
      setIsSaving(true);
      try {
        const recId = recommendation.id || recommendation.what_to_improve;
        const updatedScore = await apiClient.updateRecommendation(project._id, {
          recommendation_id: recId,
          what_to_improve: title,
          why_it_matters: why,
          how_to_improve: how,
          priority: parseInt(priority)
        });

        onUpdate(updatedScore);
        toast.success("Recommendation updated successfully");
      } catch (error: any) {
        console.error("Failed to update recommendation:", error);
        toast.error(error.message || "Failed to update recommendation");
      } finally {
        setIsSaving(false);
      }
    };

    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-cabinet text-xl">Edit Recommendation</DialogTitle>
            <DialogDescription className="font-ibm">
              Modify the recommendation details. Changes will be visible to the user immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="rec-title" className="font-ibm">Title (What to improve)</Label>
              <Input
                id="rec-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="font-ibm"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="rec-priority" className="font-ibm">Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="font-ibm">
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">High Priority (1)</SelectItem>
                  <SelectItem value="2">Medium Priority (2)</SelectItem>
                  <SelectItem value="3">Lower Priority (3)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="rec-why" className="font-ibm">Why it matters (Markdown)</Label>
              <Textarea
                id="rec-why"
                value={why}
                onChange={(e) => setWhy(e.target.value)}
                className="font-ibm min-h-[100px]"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="rec-how" className="font-ibm">How to improve (Markdown)</Label>
              <Textarea
                id="rec-how"
                value={how}
                onChange={(e) => setHow(e.target.value)}
                className="font-ibm min-h-[150px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)} disabled={isSaving} className="font-ibm">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving} className="font-ibm">
              {isSaving ? (
                <>
                  <span className="animate-spin mr-2">⏳</span> Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" /> Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-full p-3">
          <X className="h-8 w-8 text-red-500" />
        </div>
        <h2 className="text-2xl font-semibold text-gray-900 font-cabinet">Error Loading Project</h2>
        <p className="text-muted-foreground max-w-md text-center font-ibm">{error}</p>
        <Button onClick={() => navigate(-1)} variant="outline" className="font-ibm">
          <ArrowLeft className="mr-2 h-4 w-4" /> Go Back
        </Button>
      </div>
    );
  }

  if (!project) {
    return <div>Project not found</div>;
  }

  return (
    <div className="space-y-8 pb-12 font-ibm">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate(-1)} className="bg-card/60 backdrop-blur-sm hover:bg-secondary border-border">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-foreground font-cabinet">{project.title}</h1>
            <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1 font-ibm">
              <span>Created by {project.user_email}</span>
              <span>•</span>
              <span className="flex items-center">
                <Clock className="mr-1 h-3 w-3" />
                {format(new Date(project.created_at), 'PPP')}
              </span>
            </div>
            {/* Creator Interaction Tracking */}
            <div className="flex items-center gap-4 text-xs text-muted-foreground mt-2 font-ibm">
              {project.last_creator_interaction_at && (
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  Creator last active: {format(new Date(project.last_creator_interaction_at), 'MMM d, yyyy \'at\' h:mm a')}
                </span>
              )}
              {project.last_admin_interaction_at && (
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  Admin last active: {format(new Date(project.last_admin_interaction_at), 'MMM d, yyyy \'at\' h:mm a')}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4 bg-card/60 backdrop-blur-sm p-2 rounded-lg border border-border shadow-sm">
          <span className="text-sm font-medium pl-2 text-foreground font-ibm">Review Status:</span>
          <div className="flex items-center gap-2 pr-2">
            <Switch
              checked={!!project.is_reviewed}
              onCheckedChange={() => handleReviewToggle(!!project.is_reviewed)}
              className="data-[state=checked]:bg-primary"
            />
            <span className={`text-sm font-medium font-ibm ${project.is_reviewed ? "text-primary" : "text-yellow-600"}`}>
              {project.is_reviewed ? "Reviewed" : "Pending Review"}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="flex flex-col-reverse lg:grid lg:grid-cols-3 gap-6">

        {/* Left Column: Project Details (2/3) */}
        <div className="lg:col-span-2 space-y-6">

          {/* Files Card */}
          <Card className={`bg-card/60 backdrop-blur-sm border-border ${isProcessingScript || isProcessingArtifact || project.status === 'script_uploaded' || project.status === 'processing' || project.status === 'analysis_in_progress' ? 'opacity-50 pointer-events-none' : ''}`}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="flex items-center text-lg font-cabinet">
                <File className="mr-2 h-5 w-5 text-muted-foreground" />
                Files & Scripts
              </CardTitle>
              <div>
                <input
                  type="file"
                  ref={scriptInputRef}
                  className="hidden"
                  accept="application/pdf, text/plain, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={handleScriptChange}
                />
                <div className="flex items-center gap-2">
                  {isProcessingScript && (
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 animate-pulse flex items-center gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Processing Score...
                    </Badge>
                  )}
                  <Button
                    onClick={() => scriptInputRef.current?.click()}
                    disabled={isUploadingScript || isProcessingScript}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    {isUploadingScript ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Upload className="h-3 w-3" />
                    )}
                    Upload Script Override
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {project.scripts && project.scripts.length > 0 ? (
                <div className="space-y-3">
                  {project.scripts.map((script: any) => (
                    <div key={script.id || script._id} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg border border-border">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 bg-card rounded-lg border border-border flex items-center justify-center">
                          <FileText className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground font-ibm">{script.filename}</p>
                          <p className="text-xs text-muted-foreground font-ibm">
                            {(script.file_size / 1024 / 1024).toFixed(2)} MB • {format(new Date(script.created_at), 'MMM d, yyyy')}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Preview File"
                          className="hover:bg-primary/10 hover:text-primary"
                          onClick={async () => {
                            try {
                              const blob = await apiClient.downloadAdminScript(script.id || script._id, true);

                              // Check if it's a DOCX file
                              const isDocx = script.filename?.toLowerCase().trim().endsWith('.docx') ||
                                blob.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

                              if (isDocx) {
                                console.log("Opening DOCX preview for", script.filename);
                                setPreviewFile({ blob, name: script.filename });
                                setIsPreviewOpen(true);
                              } else {
                                console.log("Opening default preview for", script.filename, "type:", blob.type);
                                // Default behavior for other files (PDFs, etc.)
                                const url = URL.createObjectURL(blob);
                                window.open(url, '_blank');
                                setTimeout(() => URL.revokeObjectURL(url), 1000);
                              }
                            } catch (error) {
                              console.error('Failed to preview script:', error);
                              toast.error('Failed to preview script. Please try again.');
                            }
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Download File"
                          className="hover:bg-primary/10 hover:text-primary"
                          onClick={async () => {
                            try {
                              const blob = await apiClient.downloadAdminScript(script.id || script._id, false);
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = script.filename;
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                              URL.revokeObjectURL(url);
                              toast.success('Script downloaded successfully');
                            } catch (error) {
                              console.error('Failed to download script:', error);
                              toast.error('Failed to download script. Please try again.');
                            }
                          }}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic font-ibm">No files uploaded.</p>
              )}
            </CardContent>
          </Card>

          {/* Project Assets */}
          <Card className={`bg-card/60 backdrop-blur-sm border-border ${isProcessingScript || isProcessingArtifact || project.status === 'script_uploaded' || project.status === 'processing' || project.status === 'analysis_in_progress' ? 'opacity-50 pointer-events-none' : ''}`}>
            <CardContent className="pt-6">
              <AdminProjectAssetsSection project={project} onUpdate={fetchData} />
            </CardContent>
          </Card>

          <Card className={`bg-card/60 backdrop-blur-sm border-border ${isProcessingScript || isProcessingArtifact || project.status === 'script_uploaded' || project.status === 'processing' || project.status === 'analysis_in_progress' ? 'opacity-50 pointer-events-none' : ''}`}>
            <CardHeader>
              <CardTitle className="flex items-center text-lg font-cabinet">
                <FileText className="mr-2 h-5 w-5 text-muted-foreground" />
                Logline & Description
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {project.logline && (
                <div className="bg-secondary/30 p-4 rounded-md border border-border">
                  <span className="text-xs font-semibold uppercase text-muted-foreground block mb-1 font-ibm">Logline</span>
                  <p className="text-foreground font-medium font-ibm">{project.logline}</p>
                </div>
              )}
              <div>
                <span className="text-xs font-semibold uppercase text-muted-foreground block mb-1 font-ibm">Description</span>
                <p className="text-foreground leading-relaxed whitespace-pre-wrap font-ibm">
                  {project.description || "No description provided."}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Script Analysis (Craft Assessment) */}
          {project.artie_score?.brand_fit_analysis?.craft_assessment && (
            <Card className={`bg-card/60 backdrop-blur-sm border-border ${isProcessingScript || isProcessingArtifact || project.status === 'script_uploaded' || project.status === 'processing' || project.status === 'analysis_in_progress' ? 'opacity-50 pointer-events-none' : ''}`}>
              <CardHeader>
                <CardTitle className="flex items-center text-lg font-cabinet">
                  <ScrollText className="mr-2 h-5 w-5 text-muted-foreground" />
                  Script Analysis (Craft)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {Object.entries(project.artie_score.brand_fit_analysis.craft_assessment).map(([key, value]) => {
                    if (typeof value === 'string' && ['structure', 'hook', 'characters', 'dialogue', 'payoff'].includes(key)) {
                      return (
                        <div key={key} className="bg-secondary/30 p-3 rounded-lg border border-border">
                          <span className="text-xs text-muted-foreground uppercase font-bold block mb-1">{key}</span>
                          <Badge variant="outline" className="capitalize bg-card">{value}</Badge>
                        </div>
                      )
                    }
                    return null;
                  })}
                </div>
                {/* Strengths & Weaknesses */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                  {project.artie_score.brand_fit_analysis.craft_assessment.specific_strengths?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-green-700 mb-2 font-ibm flex items-center">
                        <CheckCircle2 className="w-4 h-4 mr-1.5" /> Strengths
                      </h4>
                      <ul className="space-y-1">
                        {project.artie_score.brand_fit_analysis.craft_assessment.specific_strengths.map((item: string, i: number) => (
                          <li key={i} className="text-sm text-muted-foreground pl-2 border-l-2 border-green-200">{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {project.artie_score.brand_fit_analysis.craft_assessment.specific_weaknesses?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-amber-700 mb-2 font-ibm flex items-center">
                        <Lightbulb className="w-4 h-4 mr-1.5" /> Areas for Improvement
                      </h4>
                      <ul className="space-y-1">
                        {project.artie_score.brand_fit_analysis.craft_assessment.specific_weaknesses.map((item: string, i: number) => (
                          <li key={i} className="text-sm text-muted-foreground pl-2 border-l-2 border-amber-200">{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recommendations (Updated Design) - Only show if V2 tactics are NOT present */}
          {project.artie_score?.recommendations?.length > 0 && !tacticsV2Data && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-semibold font-cabinet">Recommendations</h2>
              </div>
              <div className="space-y-4">
                {project.artie_score.recommendations.map((rec: any, index: number) => (
                  <RecommendationCard
                    key={index}
                    recommendation={rec}
                    projectId={project._id}
                    onAccept={() => { }}
                    onDecline={() => { }}
                    onGoDeeper={() => { }}
                    onComplete={() => { }}
                    readOnly={true}
                    onEdit={openRecEdit}
                  />
                ))}
              </div>
              {editingRec && (
                <EditRecommendationDialog
                  project={project}
                  recommendation={editingRec}
                  isOpen={isEditRecOpen}
                  setIsOpen={setIsEditRecOpen}
                  onUpdate={handleRecommendationUpdate}
                />
              )}
            </div>
          )}

          {/* Tactics V2 Recommendations (New Implementation) */}
          {tacticsV2Data && (
            <div className={`space-y-4 ${isProcessingScript || isProcessingArtifact || project.status === 'script_uploaded' || project.status === 'processing' || project.status === 'analysis_in_progress' ? 'opacity-50 pointer-events-none' : ''}`}>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-blue-600" />
                  <h2 className="text-lg font-semibold font-cabinet">All Available Tactics (Priority Mapped)</h2>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                    Phase: {tacticsV2Data.creator_phase?.replace('_', ' ').toUpperCase()}
                  </Badge>
                </div>
                <Button onClick={() => setIsAddTacticDialogOpen(true)}>Add Tactic</Button>
              </div>

              <div className="bg-blue-50/50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-900">Artie Score: {tacticsV2Data.artie_score}/100</span>
                  </div>
                  <span className="text-xs text-blue-700">
                    Showing all {tacticsV2Data.total_eligible_tactics || 0} eligible tactics
                  </span>
                </div>

                {tacticsV2Loading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-20 w-full" />
                    ))}
                  </div>
                ) : tacticsV2Data.recommendations?.length > 0 ? (
                  <div className="space-y-3">
                    {tacticsV2Data.recommendations.map((tactic: any, index: number) => (
                      <div key={tactic.tactic_id} className={`bg-white border border-blue-200 rounded-lg p-4 ${tactic.is_hidden ? 'opacity-50' : ''}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={`text-xs flex items-center gap-1 ${tactic.is_pinned ? 'bg-amber-100 text-amber-800 border-amber-200' : ''}`}>
                              {tactic.is_pinned && <Pin className="w-3 h-3 fill-current" />}
                              #{tactic.priority_rank || index + 1}
                            </Badge>
                            <Badge
                              variant="outline"
                              className={`text-xs ${tactic.dimension === 'reach' ? 'bg-green-50 text-green-700 border-green-200' :
                                tactic.dimension === 'brand_fit' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                  'bg-purple-50 text-purple-700 border-purple-200'
                                }`}
                            >
                              {tactic.dimension.replace('_', ' ')}
                            </Badge>
                            <Badge
                              variant="outline"
                              className={`text-xs ${tactic.impact_tier === 'HIGH' ? 'bg-red-50 text-red-700 border-red-200' :
                                tactic.impact_tier === 'MEDIUM' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                                  'bg-gray-50 text-gray-700 border-gray-200'
                                }`}
                            >
                              {tactic.impact_tier} Impact
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="outline"
                              className={`text-xs capitalize ${tactic.status === 'pending' ? 'bg-gray-50 text-gray-700 border-gray-200' :
                                tactic.status === 'accepted' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                  tactic.status === 'completed' ? 'bg-green-50 text-green-700 border-green-200' :
                                    'bg-red-50 text-red-700 border-red-200'
                                }`}
                            >
                              {tactic.status}
                            </Badge>

                            {/* Edit Rank Button */}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-muted-foreground hover:text-foreground"
                              onClick={() => openPinDialog(tactic)}
                              title="Edit Priority Rank"
                            >
                              <GripVertical className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-muted-foreground hover:text-foreground"
                              onClick={() => {
                                setEditingTactic(tactic);
                                setIsEditWordingDialogOpen(true);
                              }}
                              title="Edit Wording"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-muted-foreground hover:text-foreground"
                              onClick={() => {
                                setUploadingTactic(tactic);
                                setIsUploadArtifactOpen(true);
                              }}
                              title="Upload Artifact Override"
                            >
                              <Upload className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-muted-foreground hover:text-destructive"
                              onClick={() => {
                                setHidingTactic(tactic);
                                setIsHideConfirmOpen(true);
                              }}
                              title={tactic.is_hidden ? "Unhide Tactic" : "Hide Tactic"}
                            >
                              {tactic.is_hidden ? <Eye className="h-4 w-4" /> : <X className="h-4 w-4" />}
                            </Button>
                          </div>
                        </div>

                        <h4 className="font-medium text-gray-900 mb-1 font-ibm flex items-center gap-2">
                          {tactic.tactic_name}
                          {tactic.is_pinned && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-semibold border border-amber-200">PINNED</span>}
                        </h4>

                        {tactic.is_hidden && (
                          <span className="text-[10px] bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded-full font-semibold border border-gray-200 inline-block mb-1">
                            HIDDEN
                          </span>
                        )}

                        {tactic.is_pinned && tactic.pinned_reason && (
                          <div className="mb-2 p-2 bg-amber-50 border-l-2 border-amber-400 rounded-r text-xs text-amber-800">
                            <span className="font-semibold mr-1">Override Reason:</span> {tactic.pinned_reason}
                          </div>
                        )}

                        <p className="text-sm text-gray-600 mb-2 font-ibm">
                          {tactic.gap_description || tactic.reason}
                        </p>

                        {tactic.artifact && (
                          <div className="mb-3 space-y-1">
                            <h5 className="text-xs font-semibold text-gray-700 font-ibm">Uploaded Artifact:</h5>
                            <div key={tactic.artifact.id} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded p-2">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span className="text-xs font-medium text-blue-600 truncate max-w-[150px]" title={tactic.artifact.filename}>
                                  {tactic.artifact.filename}
                                </span>
                                {tactic.artifact.uploaded_by_admin ? (
                                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs shrink-0">
                                    Admin
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs shrink-0">
                                    Creator
                                  </Badge>
                                )}
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 hover:bg-blue-100 hover:text-blue-700"
                                onClick={() => window.open(tactic.artifact.url, '_blank')}
                                title="View Artifact"
                              >
                                <Eye className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        )}

                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>Priority Score: {tactic.priority_score?.toFixed(1) || 'N/A'}</span>
                          <span>
                            {tactic.created_at && format(new Date(tactic.created_at), 'MMM d, yyyy')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-blue-700">
                    <p className="text-sm">
                      {tacticsV2Data.suppression_reason || "No tactics recommendations available"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Pin Tactic Dialog */}
          <AddTacticDialog
            project={project}
            allTactics={allTactics}
            recommendations={tacticsV2Data?.recommendations || []}
            isOpen={isAddTacticDialogOpen}
            setIsOpen={setIsAddTacticDialogOpen}
            onUpdate={fetchData}
          />

          {/* Script Upload Dialog */}
          <AlertDialog open={showScriptUploadDialog} onOpenChange={(open) => {
            setShowScriptUploadDialog(open);
            if (!open) {
              setScriptToUpload(null);
              setScriptUploadReason("");
              if (scriptInputRef.current) scriptInputRef.current.value = "";
            }
          }}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Upload Script Override</AlertDialogTitle>
                <AlertDialogDescription>
                  You are about to upload a new script: "{scriptToUpload?.name}". This action will be logged and will replace the project's core script, triggering an automatic score recalculation.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="my-4">
                <Label htmlFor="script-upload-reason" className="mb-2 block font-medium">
                  Reason for Update <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="script-upload-reason"
                  placeholder="e.g., Creator emailed the revised script today"
                  value={scriptUploadReason}
                  onChange={(e) => setScriptUploadReason(e.target.value)}
                  disabled={isUploadingScript}
                />
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isUploadingScript}>Cancel</AlertDialogCancel>
                <Button
                  onClick={handleConfirmScriptUpload}
                  disabled={isUploadingScript || !scriptUploadReason.trim()}
                >
                  {isUploadingScript ? "Uploading..." : "Upload & Process"}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Docx Preview Dialog */}
          <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
            <DialogContent className="max-w-4xl h-[80vh] flex flex-col p-0">
              <DialogHeader className="px-6 py-4 border-b">
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  {previewFile?.name || "Document Preview"}
                </DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-auto bg-gray-100 p-4">
                <DocxViewer blob={previewFile?.blob} />
              </div>
            </DialogContent>
          </Dialog>

          <EditProjectDetailsDialog
            project={project}
            isOpen={isEditDetailsOpen}
            setIsOpen={setIsEditDetailsOpen}
            queryClient={queryClient}
            navigate={navigate}
          />

          <PinTacticDialog
            project={project}
            tactic={pinningTactic}
            isOpen={isPinDialogOpen}
            setIsOpen={setIsPinDialogOpen}
            onUpdate={(data: any) => {
              // Refresh is handled inside the dialog component via parent state updaters
            }}
          />
          {uploadingTactic && (
            <UploadArtifactDialog
              project={project}
              tactic={uploadingTactic}
              isOpen={isUploadArtifactOpen}
              setIsOpen={setIsUploadArtifactOpen}
              queryClient={queryClient}
              navigate={navigate}
              onUpdate={fetchData}
            />
          )}
          {editingTactic && (
            <EditTacticWordingDialog
              project={project}
              tactic={editingTactic}
              isOpen={isEditWordingDialogOpen}
              setIsOpen={setIsEditWordingDialogOpen}
              onUpdate={(updatedProject: any) => {
                setProject(updatedProject);
                refreshTacticsData(updatedProject, allTactics);
              }}
            />
          )}
          {hidingTactic && (
            <AlertDialog open={isHideConfirmOpen} onOpenChange={setIsHideConfirmOpen}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will {hidingTactic.is_hidden ? 'unhide' : 'hide'} the tactic "{hidingTactic.tactic_name}" for this project.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={async () => {
                      try {
                        await apiClient.overrideProjectTactic(project._id, hidingTactic.tactic_id, { is_hidden: !hidingTactic.is_hidden });
                        fetchData();
                        toast.success(`Tactic ${hidingTactic.is_hidden ? 'unhidden' : 'hidden'} successfully`);
                      } catch (error: any) {
                        console.error("Failed to hide tactic:", error);
                        toast.error(error.message || "Failed to hide tactic");
                      }
                    }}
                  >
                    Confirm
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>

        {/* Right Column: Metadata & Stats (1/3) */}
        <div className="space-y-6">
          {/* Status Card */}
          <Card className="bg-secondary/20 border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-muted-foreground font-ibm">Project Status</span>
                <Badge className="capitalize bg-secondary text-secondary-foreground hover:bg-secondary/80 font-ibm">
                  {project.status.replace(/_/g, ' ')}
                </Badge>
              </div>

              {project.is_reviewed && (
                <div className="flex items-center text-sm text-primary bg-primary/10 p-3 rounded-md border border-primary/20">
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  <div className="flex flex-col">
                    <span className="font-semibold font-cabinet">QA Reviewed</span>
                    <span className="text-xs opacity-80 font-ibm">
                      {project.reviewed_at ? format(new Date(project.reviewed_at), 'MMM d, yyyy') : ''}
                    </span>
                  </div>
                </div>
              )}

              {(isProcessingScript || isProcessingArtifact || project.status === 'script_uploaded' || project.status === 'processing' || project.status === 'analysis_in_progress') && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                  <div className="flex items-center text-sm text-amber-700 mb-1 font-semibold">
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Score Calculation in Progress
                  </div>
                  {projectUpdates ? (
                    <div className="text-xs text-amber-600 pl-6 mt-1 font-ibm truncate">
                      {projectUpdates?.status || "Processing..."}
                      {projectUpdates?.percent > 0 && ` (${projectUpdates.percent}%)`}
                    </div>
                  ) : (
                    <div className="text-xs text-amber-600 pl-6 mt-1 font-ibm truncate">
                      Processing...
                    </div>
                  )}
                </div>

              )}
            </CardContent>
          </Card>

          {/* Artie Score MVP */}
          <Card className="bg-gradient-to-br from-primary/5 to-card border-primary/20">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base flex items-center gap-2 font-cabinet">
                <Target className="h-4 w-4 text-primary" />
                Artie Score
              </CardTitle>
              {project.artie_score && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsEditScoreOpen(true)}
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    title="Edit Score"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <EditScoreDialog
                    project={project}
                    isOpen={isEditScoreOpen}
                    setIsOpen={setIsEditScoreOpen}
                    onScoreUpdate={handleScoreUpdate}
                  />
                </>
              )}
            </CardHeader>
            <CardContent>
              {project.artie_score ? (
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b border-primary/10">
                    <div className="flex flex-col">
                      <span className="text-sm text-muted-foreground font-ibm">Total Score</span>
                      {project.artie_score.is_overridden && (
                        <span className="text-[10px] text-amber-600 font-medium flex items-center mt-1">
                          <Pencil className="w-3 h-3 mr-1" /> Manually Overridden
                        </span>
                      )}
                    </div>
                    <Badge className={`${project.artie_score.is_overridden ? 'bg-amber-600 hover:bg-amber-700' : 'bg-primary hover:bg-primary/90'} text-lg px-3 py-1 font-mono`}>
                      {project.artie_score.artie_score}/100
                    </Badge>
                  </div>

                  <div className="space-y-4 pt-2">
                    {/* Sub-scores Grid */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-secondary/20 p-3 rounded-lg border border-border">
                        <span className="text-xs text-muted-foreground uppercase block mb-2 font-ibm font-semibold">Reach Potential</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={`capitalize font-medium ${project.artie_score.reach_status === 'green' ? 'bg-green-50 text-green-700 border-green-200' : project.artie_score.reach_status === 'yellow' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                            {project.artie_score.reach_status || 'N/A'}
                          </Badge>
                          <div className={`w-2 h-2 rounded-full ${project.artie_score.reach_status === 'green' ? 'bg-green-500' : project.artie_score.reach_status === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'}`}></div>
                        </div>
                        {project.artie_score.reach_analysis?.current_subscribers && (
                          <p className="text-xs text-muted-foreground mt-1 font-ibm">
                            {project.artie_score.reach_analysis.current_subscribers.toLocaleString()} subscribers
                          </p>
                        )}
                      </div>
                      <div className="bg-secondary/20 p-3 rounded-lg border border-border">
                        <span className="text-xs text-muted-foreground uppercase block mb-2 font-ibm font-semibold">Brand Fit</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={`capitalize font-medium ${project.artie_score.brand_fit_status === 'green' ? 'bg-green-50 text-green-700 border-green-200' : project.artie_score.brand_fit_status === 'yellow' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                            {project.artie_score.brand_fit_status || 'N/A'}
                          </Badge>
                          <div className={`w-2 h-2 rounded-full ${project.artie_score.brand_fit_status === 'green' ? 'bg-green-500' : project.artie_score.brand_fit_status === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'}`}></div>
                        </div>
                        {project.artie_score.brand_fit_analysis?.accessible_brand_pool && (
                          <p className="text-xs text-muted-foreground mt-1 font-ibm">
                            ~{project.artie_score.brand_fit_analysis.accessible_brand_pool} accessible brands
                          </p>
                        )}
                      </div>
                      <div className="bg-secondary/20 p-3 rounded-lg border border-border">
                        <span className="text-xs text-muted-foreground uppercase block mb-2 font-ibm font-semibold">Production Readiness</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={`capitalize font-medium ${project.artie_score.production_readiness_status === 'green' ? 'bg-green-50 text-green-700 border-green-200' : project.artie_score.production_readiness_status === 'yellow' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                            {project.artie_score.production_readiness_status || 'N/A'}
                          </Badge>
                          <div className={`w-2 h-2 rounded-full ${project.artie_score.production_readiness_status === 'green' ? 'bg-green-500' : project.artie_score.production_readiness_status === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'}`}></div>
                        </div>
                        {project.artie_score.brand_fit_analysis?.craft_assessment?.overall_craft_level && (
                          <p className="text-xs text-muted-foreground mt-1 font-ibm capitalize">
                            {project.artie_score.brand_fit_analysis.craft_assessment.overall_craft_level.replace('_', ' ')} craft
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Budget Viability */}
                    {project.artie_score.budget_viability && (
                      <div className="bg-secondary/10 p-3 rounded-lg border border-border">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground uppercase font-ibm font-semibold">Budget Viability</span>
                          <Badge variant="outline" className={`capitalize font-medium ${project.artie_score.budget_viability === 'achievable' ? 'bg-green-50 text-green-700 border-green-200' : project.artie_score.budget_viability === 'stretch' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                            {project.artie_score.budget_viability}
                          </Badge>
                        </div>
                        {project.artie_score.budget_viability_explanation && (
                          <p className="text-xs text-muted-foreground mt-2 font-ibm">
                            {project.artie_score.budget_viability_explanation}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {project.artie_score.override_reason && (
                    <div className="bg-amber-50 border border-amber-100 p-3 rounded-md text-xs text-amber-800 mt-2">
                      <span className="font-semibold block mb-1">Override Reason:</span>
                      {project.artie_score.override_reason}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4 text-muted-foreground font-ibm">
                  <p className="text-sm">No Artie Score generated yet.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Key Metrics */}
          <Card className="bg-card/60 backdrop-blur-sm border-border relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 h-8 w-8 text-muted-foreground hover:text-foreground"
              onClick={() => setIsEditDetailsOpen(true)}
              title="Edit Project Details"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <CardHeader>
              <CardTitle className="text-base font-cabinet">Key Metrics</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Budget Target */}
              <div className="flex justify-between items-center py-2 border-b border-border">
                <div className="flex items-center text-sm text-muted-foreground font-ibm">
                  <DollarSign className="w-4 h-4 mr-2" /> Budget Target
                </div>
                <span className="font-semibold font-ibm">${project.artie_score?.budget_target?.toLocaleString() || project.budget?.toLocaleString() || '0'}</span>
              </div>

              {/* Potential Sponsorship Value (PSV) */}
              {project.artie_score?.psv_calculation && (
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <div className="flex items-center text-sm text-muted-foreground font-ibm">
                    <Sparkles className="w-4 h-4 mr-2" /> Potential Sponsorship Value
                  </div>
                  <span className="font-semibold font-ibm text-primary">${project.artie_score.psv_calculation.psv_final?.toLocaleString() || 'N/A'}</span>
                </div>
              )}

              {/* Current Subscribers */}
              {project.artie_score?.reach_analysis?.current_subscribers && (
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <div className="flex items-center text-sm text-muted-foreground font-ibm">
                    <Users className="w-4 h-4 mr-2" /> Current Subscribers
                  </div>
                  <span className="font-semibold font-ibm">{project.artie_score.reach_analysis.current_subscribers.toLocaleString()}</span>
                </div>
              )}

              {/* Average Views */}
              {project.artie_score?.reach_analysis?.avg_views && (
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <div className="flex items-center text-sm text-muted-foreground font-ibm">
                    <Eye className="w-4 h-4 mr-2" /> Average Views
                  </div>
                  <span className="font-semibold font-ibm">{project.artie_score.reach_analysis.avg_views.toLocaleString()}</span>
                </div>
              )}

              {/* Expected Reach Range */}
              {project.artie_score?.reach_analysis?.expected_reach_range && (
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <div className="flex items-center text-sm text-muted-foreground font-ibm">
                    <Target className="w-4 h-4 mr-2" /> Expected Reach
                  </div>
                  <span className="font-semibold font-ibm">
                    {project.artie_score.reach_analysis.expected_reach_range.low?.toLocaleString()} - {project.artie_score.reach_analysis.expected_reach_range.high?.toLocaleString()}
                  </span>
                </div>
              )}

              {/* Accessible Brand Pool */}
              {project.artie_score?.brand_fit_analysis?.accessible_brand_pool && (
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <div className="flex items-center text-sm text-muted-foreground font-ibm">
                    <Layers className="w-4 h-4 mr-2" /> Accessible Brands
                  </div>
                  <span className="font-semibold font-ibm">~{project.artie_score.brand_fit_analysis.accessible_brand_pool}</span>
                </div>
              )}

              {/* Distribution Strategy */}
              {project.distribution_strategy && project.distribution_strategy.length > 0 && (
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <div className="flex items-center text-sm text-muted-foreground font-ibm">
                    <Globe className="w-4 h-4 mr-2" /> Distribution
                  </div>
                  <span className="font-semibold font-ibm text-right">{project.distribution_strategy.join(", ")}</span>
                </div>
              )}

              {/* Integration Opportunities */}
              {project.artie_score?.brand_fit_analysis?.integration_opportunities && (
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <div className="flex items-center text-sm text-muted-foreground font-ibm">
                    <Sparkles className="w-4 h-4 mr-2" /> Integration Opportunities
                  </div>
                  <span className="font-semibold font-ibm">
                    {project.artie_score.brand_fit_analysis.integration_opportunities.visible_logo_opportunities +
                      project.artie_score.brand_fit_analysis.integration_opportunities.product_mention_opportunities +
                      project.artie_score.brand_fit_analysis.integration_opportunities.product_use_opportunities} total
                  </span>
                </div>
              )}

              {/* Optimization Goal */}
              <div className="flex justify-between items-center py-2">
                <div className="flex items-center text-sm text-muted-foreground font-ibm">
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Goal
                </div>
                <span className="font-semibold capitalize font-ibm">{project.optimization_goal?.replace(/_/g, ' ') || 'Find Brand Partners'}</span>
              </div>
            </CardContent>
          </Card>

          {/* Integration Strategy (Moved Here) */}
          <Card className="bg-card/60 backdrop-blur-sm border-border">
            <CardHeader>
              <CardTitle className="flex items-center text-base font-cabinet">
                <Layers className="mr-2 h-4 w-4 text-muted-foreground" />
                Integration Strategy
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="text-xs font-medium text-muted-foreground mb-2 font-ibm uppercase">Integration Types</h4>
                <div className="flex flex-wrap gap-2">
                  {project.integration_types?.length > 0 ? (
                    project.integration_types.map((type: string) => (
                      <Badge key={type} variant="secondary" className="capitalize bg-secondary text-secondary-foreground font-ibm text-xs">
                        {type.replace(/_/g, ' ')}
                      </Badge>
                    ))
                  ) : <span className="text-sm text-muted-foreground font-ibm">None specified</span>}
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="text-xs font-medium text-muted-foreground mb-2 font-ibm uppercase">Territories</h4>
                  <div className="flex flex-wrap gap-1">
                    {project.territories?.map((t: string) => (
                      <span key={t} className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-medium bg-primary/10 text-primary font-ibm border border-primary/20">
                        <Globe className="w-3 h-3 mr-1" /> {t}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-xs font-medium text-muted-foreground mb-2 font-ibm uppercase">Demographics</h4>
                  <div className="flex flex-wrap gap-1">
                    {project.demographics?.map((d: string) => (
                      <Badge key={d} variant="outline" className="text-[10px] font-ibm">{d}</Badge>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Categories */}
          <Card className="bg-card/60 backdrop-blur-sm border-border">
            <CardHeader>
              <CardTitle className="text-base font-cabinet">Categories</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <span className="text-xs text-muted-foreground uppercase font-ibm">Format</span>
                  <p className="font-medium font-ibm">{project.format || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground uppercase font-ibm">Genres</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {project.genres?.map((g: string) => (
                      <Badge key={g} variant="secondary" className="font-ibm">{g}</Badge>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

      </div>
    </div>
  );
}

function AddTacticDialog({ project, allTactics, recommendations, isOpen, setIsOpen, onUpdate }: { project: any, allTactics: any[], recommendations: any[], isOpen: boolean, setIsOpen: (open: boolean) => void, onUpdate: () => void }) {
  const [selectedTactic, setSelectedTactic] = useState<string>("");
  const [rank, setRank] = useState<number>(1);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!project?._id || !selectedTactic) return;

    if (recommendations.some(rec => rec.tactic_id === selectedTactic)) {
      toast.error("This tactic is already in the recommendation list.");
      return;
    }

    setIsSaving(true);
    try {
      await apiClient.pinTactic(project._id, selectedTactic, rank, "Manually added by admin");
      onUpdate();
      toast.success("Tactic added successfully");
      setIsOpen(false);
      setSelectedTactic("");
    } catch (error: any) {
      console.error("Failed to add tactic:", error);
      toast.error(error.message || "Failed to add tactic");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="font-cabinet text-xl">Add Tactic</DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="tactic-select">Tactic</Label>
            <Select onValueChange={setSelectedTactic}>
              <SelectTrigger id="tactic-select">
                <SelectValue placeholder="Select a tactic" />
              </SelectTrigger>
              <SelectContent>
                {allTactics.map((tactic) => (
                  <SelectItem key={tactic._id} value={tactic._id}>
                    {tactic.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="rank-input">Rank</Label>
            <Input
              id="rank-input"
              type="number"
              value={rank}
              onChange={(e) => setRank(parseInt(e.target.value))}
              min="1"
              max="10"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)} disabled={isSaving}>Cancel</Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditTacticWordingDialog({ project, tactic, isOpen, setIsOpen, onUpdate }: { project: any, tactic: any, isOpen: boolean, setIsOpen: (open: boolean) => void, onUpdate: (data: any) => void }) {
  const [wording, setWording] = useState<string>(tactic?.gap_description || "");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen && tactic) {
      setWording(tactic.gap_description || "");
    }
  }, [isOpen, tactic]);

  const handleSave = async () => {
    if (!project?._id || !tactic?.tactic_id) return;

    setIsSaving(true);
    try {
      await apiClient.overrideProjectTactic(project._id, tactic.tactic_id, { custom_wording: wording });

      // Refresh project data to get updated tactics
      const updatedProject = await apiClient.getAdminProject(project._id);
      onUpdate(updatedProject);

      toast.success("Tactic wording updated successfully");
      setIsOpen(false);
    } catch (error: any) {
      console.error("Failed to update tactic wording:", error);
      toast.error(error.message || "Failed to update tactic wording");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="font-cabinet text-xl">Edit Tactic Wording</DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <Textarea
            value={wording}
            onChange={(e) => setWording(e.target.value)}
            className="min-h-[120px]"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)} disabled={isSaving}>Cancel</Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditProjectDetailsDialog({ project, isOpen, setIsOpen, queryClient, navigate }: { project: any, isOpen: boolean, setIsOpen: (open: boolean) => void, queryClient: any, navigate: any }) {
  const [budget, setBudget] = useState<string>(project.artie_score?.budget_target?.toString() || project.budget?.toString() || "");
  const [distribution, setDistribution] = useState<string>(project.distribution_strategy?.join(", ") || "");
  const [reason, setReason] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen && project) {
      setBudget(project.artie_score?.budget_target?.toString() || project.budget?.toString() || "");
      setDistribution(project.distribution_strategy?.join(", ") || "");
    }
  }, [isOpen, project]);

  const handleSave = async () => {
    if (!project?._id) return;
    if (!reason.trim()) {
      toast.error("Please provide a reason for this override");
      return;
    }

    setIsSaving(true);
    try {
      const parsedBudget = budget.trim() ? parseInt(budget, 10) : undefined;
      const parsedDistribution = distribution.trim() ? distribution.split(",").map(s => s.trim()).filter(Boolean) : undefined;

      await apiClient.updateProjectDetailsOverride(project._id, {
        budget: parsedBudget,
        distribution_strategy: parsedDistribution,
        reason
      });

      toast.success("Project details updated successfully and score recalculation triggered");
      queryClient.invalidateQueries({ queryKey: ["adminProjects"] });
      setIsOpen(false);
      setReason("");
      navigate("/admin/projects");
    } catch (error: any) {
      console.error("Failed to update project details:", error);
      toast.error(error.message || "Failed to update project details");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      setIsOpen(open);
      if (!open) {
        setReason("");
      }
    }}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="font-cabinet text-xl flex items-center gap-2">
            <Pencil className="h-5 w-5" />
            Edit Project Details
          </DialogTitle>
          <DialogDescription className="font-ibm">
            Update core project details. This action will be logged and trigger an automatic score recalculation.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Budget ($)</Label>
            <Input
              type="number"
              placeholder="e.g. 500000"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              disabled={isSaving}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Distribution Strategy</Label>
            <Textarea
              placeholder="Comma-separated (e.g. Theatrical, Streaming, YouTube)"
              value={distribution}
              onChange={(e) => setDistribution(e.target.value)}
              disabled={isSaving}
              className="min-h-[60px]"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Reason for Override <span className="text-red-500">*</span></Label>
            <Textarea
              placeholder="Why are you changing these details? e.g., 'Creator provided updated budget via email'"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="min-h-[80px]"
              disabled={isSaving}
            />
          </div>
        </div>

        <DialogFooter className="flex gap-2 sm:justify-end">
          <Button variant="outline" size="sm" onClick={() => setIsOpen(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={isSaving || !reason.trim()}>
            {isSaving ? "Saving..." : "Save & Recalculate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DocxViewer({ blob }: { blob: Blob | undefined }) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current && blob) {
      containerRef.current.innerHTML = ''; // Clear previous content
      // Pass undefined for styleContainer to use default behavior or avoid conflict
      renderAsync(blob, containerRef.current, undefined, {
        className: "docx-viewer",
        inWrapper: true,
        ignoreWidth: false,
        ignoreHeight: false,
        ignoreFonts: false,
        breakPages: true,
        ignoreLastRenderedPageBreak: true,
        experimental: false,
        trimXmlDeclaration: true,
        useBase64URL: false,
        debug: false
      })
        .then(() => console.log("DOCX rendered"))
        .catch(err => console.error("DOCX render error:", err));
    }
  }, [blob]);

  return (
    <div
      ref={containerRef}
      className="bg-white shadow-lg min-h-full p-8 mx-auto max-w-[800px]"
    />
  );
}
